import PropTypes from "prop-types";
import {
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { riskLevelColor } from "@/lib/listData";

/**
 * A 5x5 matrix showing risk levels. Highlights the selected cell.
 * @param {{receivedSeverity: number, receivedProbability: number}} props
 */
const SquareMatrix = ({ receivedSeverity, receivedProbability }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Responsive cell size with scaling
  const baseCellSize = 32;
  const cellSize = isMobile
    ? baseCellSize * 0.625
    : isTablet
      ? baseCellSize * 0.75
      : baseCellSize;
  const fontSize = isMobile ? 8 : isTablet ? 9 : 10;

  // Responsive scaling for text and labels
  const textScale = isMobile ? 0.625 : isTablet ? 0.75 : 1;

  const severityLabels = [
    t("squareMatrix.severityVeryLow"),
    t("squareMatrix.severityLow"),
    t("squareMatrix.severityMedium"),
    t("squareMatrix.severityHigh"),
    t("squareMatrix.severityVeryHigh"),
  ];

  return (
    <Box>
      {/* Matrix Labels */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box sx={{ width: cellSize + 4, height: cellSize + 4 }} />{" "}
        {/* Corner spacer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: `${0.75 * textScale}rem`,
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {t("squareMatrix.severityLabel")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {severityLabels.map((label, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  fontSize: `${0.6 * textScale}rem`,
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                  width: cellSize,
                  lineHeight: 1.2,
                }}
              >
                {isMobile ? label.split(" ")[0] : label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Probability Label (Vertical) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mr: 1,
            height: cellSize * 5 + 4,
          }}
        >
          <Box
            sx={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: `${0.75 * textScale}rem`,
                fontWeight: 600,
                color: theme.palette.text.primary,
                transform: "rotate(180deg)",
              }}
            >
              {t("squareMatrix.probabilityLabel")}
            </Typography>
          </Box>
        </Box>

        {/* Main Matrix - Rotated 90 degrees */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "2px",
            padding: 1,
            backgroundColor: theme.palette.divider,
            borderRadius: 1,
            boxShadow: theme.shadows[2],
          }}
        >
          {Array.from({ length: 5 }).map((_, row) =>
            Array.from({ length: 5 }).map((_, col) => {
              // Rotated: row = probability (1 at top, 5 at bottom), col = severity (1 at left, 5 at right)
              const probability = row + 1;
              const severity = col + 1;
              const style = riskLevelColor[severity]?.[probability] || {
                backgroundColor: theme.palette.grey[300],
                color: theme.palette.text.primary,
              };
              const isSelected =
                receivedSeverity === severity &&
                receivedProbability === probability;

              return (
                <Tooltip
                  key={`${severity}-${probability}`}
                  title={t("squareMatrix.tooltip", { severity, probability })}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      width: cellSize,
                      height: cellSize,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: fontSize,
                      fontWeight: isSelected ? 700 : 600,
                      backgroundColor: style.backgroundColor,
                      color: style.color,
                      cursor: "pointer",
                      borderRadius: 0.5,
                      transition: "all 0.3s ease",
                      border: isSelected
                        ? `3px solid ${theme.palette.primary.main}`
                        : "1px solid transparent",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": isSelected
                        ? {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(45deg, transparent 30%, ${theme.palette.primary.main}20 50%, transparent 70%)`,
                            animation: "shimmer 1.5s ease-in-out infinite",
                            "@keyframes shimmer": {
                              "0%": { transform: "translateX(-100%)" },
                              "100%": { transform: "translateX(100%)" },
                            },
                          }
                        : {},
                      "&:hover": {
                        transform: "scale(1.1)",
                        zIndex: 10,
                        boxShadow: theme.shadows[4],
                        border: `2px solid ${theme.palette.text.primary}`,
                      },
                    }}
                  >
                    {/* Show coordinates on mobile, hide on larger screens for cleaner look */}
                    {isMobile ? "" : ""}
                  </Box>
                </Tooltip>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

SquareMatrix.propTypes = {
  receivedSeverity: PropTypes.number,
  receivedProbability: PropTypes.number,
};

export default SquareMatrix;
